import React from 'react';

import { apiService } from '../../../services/api';
import { getReportDownload } from '../lib/downloadReport';
import { getReportCopy } from '../lib/reportCopy';
import {
  isReportJobActive,
  selectReportJobs,
  selectUpdateReportJob,
  useReportJobStore,
} from '../store/reportJob.store';

const DEFAULT_POLL_INTERVAL_MS = 1500;
const DEFAULT_MAX_INTERVAL_MS = 8000;
const DEFAULT_MAX_DURATION_MS = 60000;

export interface ReportJobMonitorProps {
  pollIntervalMs?: number;
  maxIntervalMs?: number;
  maxDurationMs?: number;
}

export const ReportJobMonitor: React.FC<ReportJobMonitorProps> = ({
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  maxIntervalMs = DEFAULT_MAX_INTERVAL_MS,
  maxDurationMs = DEFAULT_MAX_DURATION_MS,
}) => {
  const jobs = useReportJobStore(selectReportJobs);
  const updateReportJob = useReportJobStore(selectUpdateReportJob);
  const copy = React.useMemo(() => getReportCopy('es-ES'), []);

  const activeJobs = React.useMemo(
    () =>
      Object.values(jobs).filter(
        (job) => isReportJobActive(job.status) && Boolean(job.jobId),
      ),
    [jobs],
  );

  const activeJobsRef = React.useRef(activeJobs);
  const failureAttemptsRef = React.useRef(new Map<string, number>());
  const firstObservedAtRef = React.useRef(new Map<string, number>());

  React.useEffect(() => {
    activeJobsRef.current = activeJobs;
  }, [activeJobs]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || activeJobs.length === 0) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId: number | null = null;

    const poll = async () => {
      let highestFailureAttempt = 0;

      await Promise.all(
        activeJobsRef.current.map(async (job) => {
          const parsedStart = job.startedAt ? Date.parse(job.startedAt) : NaN;
          if (!firstObservedAtRef.current.has(job.jobId)) {
            firstObservedAtRef.current.set(job.jobId, Date.now());
          }
          const startedAt = Number.isNaN(parsedStart)
            ? (firstObservedAtRef.current.get(job.jobId) ?? Date.now())
            : parsedStart;

          if (Date.now() - startedAt >= maxDurationMs) {
            failureAttemptsRef.current.delete(job.jobId);
            firstObservedAtRef.current.delete(job.jobId);
            updateReportJob(job.jobId, {
              status: 'failed',
              error: copy.errors.timeout,
            });
            return;
          }

          try {
            const response = await apiService.getMarketingReportJob(job.jobId);
            if (cancelled) return;

            if (response.status === 'ready') {
              const download = await getReportDownload(job.jobId);
              if (cancelled) return;

              failureAttemptsRef.current.delete(job.jobId);
              firstObservedAtRef.current.delete(job.jobId);
              updateReportJob(job.jobId, {
                status: 'ready',
                downloadUrl: download.downloadUrl,
                expiresAt: download.expiresAt ?? null,
                error: null,
              });
              return;
            }

            failureAttemptsRef.current.delete(job.jobId);
            if (response.status === 'failed') {
              firstObservedAtRef.current.delete(job.jobId);
            }
            updateReportJob(job.jobId, {
              status: response.status,
              downloadUrl: null,
              expiresAt: null,
              error: response.errorMessage ?? null,
            });
          } catch (error) {
            if (cancelled) return;

            const nextAttempt =
              (failureAttemptsRef.current.get(job.jobId) ?? 0) + 1;
            failureAttemptsRef.current.set(job.jobId, nextAttempt);
            highestFailureAttempt = Math.max(
              highestFailureAttempt,
              nextAttempt,
            );
            updateReportJob(job.jobId, {
              error:
                error instanceof Error ? error.message : copy.errors.polling,
            });
          }
        }),
      );

      if (cancelled || activeJobsRef.current.length === 0) {
        return;
      }

      const delay = Math.min(
        pollIntervalMs * Math.pow(2, highestFailureAttempt),
        maxIntervalMs,
      );
      timeoutId = window.setTimeout(() => {
        void poll();
      }, delay);
    };

    void poll();

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    activeJobs.length,
    copy.errors.polling,
    copy.errors.timeout,
    maxDurationMs,
    maxIntervalMs,
    pollIntervalMs,
    updateReportJob,
  ]);

  return null;
};
